/* The science: the visitor's print laid over NASA JPL's Solar System Simulator
   (sections/pp-science.liquid, round 15).

   NASA's picture is the simulator's own 800x450 image for the date, loaded
   straight from space.jpl.nasa.gov (its form submits to a plain GET address).
   It looks down on the solar system from a fixed point 147 AU above the Sun:
   the Sun sits at the centre, 0 degrees longitude at the top, counter-
   clockwise, so a planet x AU from the Sun lands 400 / tan(fov / 2) * x / 147
   pixels from the centre (measured on all nine planets, 2026-10-29, within
   about a pixel). The print is the live engine (assets/pp-orrery.js), moved
   and scaled so its Sun sits on NASA's Sun and the chosen planet's dot on
   NASA's planet. Only directions are shared by every planet at once: the
   print spaces its rings evenly, NASA draws true distances.

   "All" asks NASA for the whole system (60 degrees) and scales the print to
   Neptune; a single planet asks for the field that puts that planet 180 px
   from the Sun, scales the print to that planet's dot, dims the other rings
   and rings the spot in gold. */
(function () {
  'use strict';

  var SIM = 'https://space.jpl.nasa.gov/cgi-bin/wspace';
  var NASA_W = 800, NASA_H = 450, NASA_D = 147;   /* image size; camera height in AU */
  var TARGET_PX = 180;                            /* where a single planet lands */
  var ALL_FOV = 60;
  var FORM_FOVS = [120, 90, 60, 45, 30, 20, 10, 5, 2, 1, 0.5, 0.2, 0.1];
  var FORM_YEARS = [1970, 2050];
  var MON = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  var TIMEOUT_MS = 20000;
  var RAD = Math.PI / 180;
  var reduce = window.matchMedia ? window.matchMedia('(prefers-reduced-motion: reduce)') : { matches: false };
  var MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July',
                'August', 'September', 'October', 'November', 'December'];

  /* JPL's approximate Keplerian elements, the same table as pp-orrery.js and
     design-generator/utils/ephemeris.js (a, e, i, L, long. perihelion, node at
     J2000, then the rate per Julian century). Here for the distances, which
     the engine never needs. */
  var ELEMENTS = {
    mercury: [[0.38709927, 0.20563593, 7.00497902, 252.25032350, 77.45779628, 48.33076593],
              [0.00000037, 0.00001906, -0.00594749, 149472.67411175, 0.16047689, -0.12534081]],
    venus:   [[0.72333566, 0.00677672, 3.39467605, 181.97909950, 131.60246718, 76.67984255],
              [0.00000390, -0.00004107, -0.00078890, 58517.81538729, 0.00268329, -0.27769418]],
    earth:   [[1.00000261, 0.01671123, -0.00001531, 100.46457166, 102.93768193, 0.0],
              [0.00000562, -0.00004392, -0.01294668, 35999.37244981, 0.32327364, 0.0]],
    mars:    [[1.52371034, 0.09339410, 1.84969142, -4.55343205, -23.94362959, 49.55953891],
              [0.00001847, 0.00007882, -0.00813131, 19140.30268499, 0.44441088, -0.29257343]],
    jupiter: [[5.20288700, 0.04838624, 1.30439695, 34.39644051, 14.72847983, 100.47390909],
              [-0.00011607, -0.00013253, -0.00183714, 3034.74612775, 0.21252668, 0.20469106]],
    saturn:  [[9.53667594, 0.05386179, 2.48599187, 49.95424423, 92.59887831, 113.66242448],
              [-0.00125060, -0.00050991, 0.00193609, 1222.49362201, -0.41897216, -0.28867794]],
    uranus:  [[19.18916464, 0.04725744, 0.77263783, 313.23810451, 170.95427630, 74.01692503],
              [-0.00196176, -0.00004397, -0.00242939, 428.48202785, 0.40805281, 0.04240589]],
    neptune: [[30.06992276, 0.00859048, 1.77004347, -55.12002969, 44.96476227, 131.78422574],
              [0.00026291, 0.00005105, 0.00035372, 218.45945325, -0.32241464, -0.00508664]],
    pluto:   [[39.48211675, 0.24882730, 17.14001206, 238.92903833, 224.06891629, 110.30393684],
              [-0.00031596, 0.00005170, 0.00004818, 145.20780515, -0.04062942, -0.01183482]]
  };

  /* The print's geometry, from pp-orrery.js: the Sun's centre sits (45, 5)
     from the inner origin, rings are drawn at INNER_SCALE, and a dot lies on
     its ring's inner border edge (3.2 px border). */
  var SUN_C = { x: 45, y: 5 };

  function each(list, fn) { Array.prototype.forEach.call(list, fn); }

  function parse(v) {
    var m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(v || '').trim());
    if (!m) return null;
    var y = +m[1], mo = +m[2], d = +m[3];
    if (mo < 1 || mo > 12 || d < 1 || d > new Date(Date.UTC(y, mo, 0)).getUTCDate()) return null;
    return { iso: m[0], y: y, m: mo, d: d };
  }

  function longDate(p) { return p.d + ' ' + MONTHS[p.m - 1] + ' ' + p.y; }

  /* Heliocentric ecliptic x, y in AU at 12:00 UTC on the date. */
  function position(name, p) {
    var el = ELEMENTS[name], b = el[0], c = el[1];
    var jd = Date.UTC(p.y, p.m - 1, p.d, 12) / 86400000 + 2440587.5;
    var t = (jd - 2451545.0) / 36525.0;
    var a = b[0] + c[0] * t, e = b[1] + c[1] * t;
    var inc = (b[2] + c[2] * t) * RAD;
    var L = b[3] + c[3] * t, lp = b[4] + c[4] * t;
    var node = (b[5] + c[5] * t) * RAD;
    var w = lp * RAD - node;
    var M = ((((L - lp) % 360) + 360) % 360) * RAD, E = M;
    for (var k = 0; k < 12; k++) {
      var step = (E - e * Math.sin(E) - M) / (1 - e * Math.cos(E));
      E -= step;
      if (Math.abs(step) < 1e-12) break;
    }
    var xo = a * (Math.cos(E) - e), yo = a * Math.sqrt(Math.max(0, 1 - e * e)) * Math.sin(E);
    var cw = Math.cos(w), sw = Math.sin(w), co = Math.cos(node), so = Math.sin(node), ci = Math.cos(inc);
    return {
      x: (cw * co - sw * so * ci) * xo + (-sw * co - cw * so * ci) * yo,
      y: (cw * so + sw * co * ci) * xo + (-sw * so + cw * co * ci) * yo
    };
  }

  /* NASA pixels per AU for a field of view. */
  function pxPerAU(fov) { return NASA_W / 2 / Math.tan(fov / 2 * RAD) / NASA_D; }

  function simURL(p, fov) {
    return SIM + '?tbody=1000&vbody=1001' +
      '&month=' + p.m + '&day=' + p.d + '&year=' + p.y +
      '&hour=12&minute=00&fovmul=1&rfov=' + fov + '&bfov=30&porbs=1&showac=1';
  }

  /* How far the print's dot sits from its Sun, in stage pixels, for a planet
     at longitude a (the ray from the Sun meets the ring, as in ringAngle). */
  function dotDistance(O, name, a) {
    var body = null;
    each(O.BODIES, function (b) { if (b.name === name) body = b; });
    var cx = body.ml + body.orb / 2 - SUN_C.x, cy = body.mt + body.orb / 2 - SUN_C.y, r = body.orb / 2 - 3.2;
    var u = a * RAD, ux = -Math.sin(u), uy = -Math.cos(u);
    var bb = -(ux * cx + uy * cy), cc = cx * cx + cy * cy - r * r;
    return (-bb + Math.sqrt(bb * bb - cc)) * O.INNER_SCALE;
  }

  /* The field of view that puts a planet dist AU out TARGET_PX from the Sun. */
  function fovFor(dist, planet) {
    if (planet === 'all') return ALL_FOV;
    return Math.round(2 * Math.atan(NASA_W / 2 * dist / (TARGET_PX * NASA_D)) / RAD * 100) / 100;
  }

  /* NASA's form only offers these, so a visitor checking there gets the
     smallest one that still holds the planet. */
  function formFov(p, planet) {
    if (planet === 'all') return ALL_FOV;
    var pos = position(planet, p);
    var want = fovFor(Math.sqrt(pos.x * pos.x + pos.y * pos.y), planet);
    var pick = FORM_FOVS[0];
    each(FORM_FOVS, function (f) { if (f >= want) pick = f; });
    return pick;
  }

  /* What to ask NASA for and where the print goes, in NASA's 800x450 frame. */
  function plan(O, p, planet) {
    var ref = planet === 'all' ? 'neptune' : planet;
    var pos = position(ref, p);
    var dist = Math.sqrt(pos.x * pos.x + pos.y * pos.y);
    var fov = fovFor(dist, planet);
    var R = dist * pxPerAU(fov);                     /* NASA px from the Sun */
    var lon = O.anglesFor(p.iso)[ref];
    var d = dotDistance(O, ref, lon);                /* stage px from the print's Sun */
    var sun = {
      x: O.ORIGIN.x + SUN_C.x * O.INNER_SCALE,
      y: O.ORIGIN.y + SUN_C.y * O.INNER_SCALE
    };
    /* PPOrrery maps the stage with k = min(W/2400, H/3000) * zoom, centred
       on the stage's middle, offset by the view box. With a 16:9 host that
       min is H/3000, so these numbers hold at any size. */
    var k800 = R / d;                                /* stage px -> NASA px */
    return {
      fov: fov,
      zoom: k800 * O.STAGE.h / NASA_H,
      viewBox: {
        x: k800 * (O.STAGE.w / 2 - sun.x) / NASA_W,
        y: k800 * (O.STAGE.h / 2 - sun.y) / NASA_H,
        w: 1,
        h: 1
      },
      mark: planet === 'all' ? null : {
        x: (NASA_W / 2 - R * Math.sin(lon * RAD)) / NASA_W,
        y: (NASA_H / 2 - R * Math.cos(lon * RAD)) / NASA_H
      }
    };
  }

  function init(root) {
    if (root.ppvReady) return;
    root.ppvReady = true;

    var cfg = {};
    try { cfg = JSON.parse(root.getAttribute('data-ppv-config') || '{}'); } catch (e) { cfg = {}; }
    var L = cfg.labels || {};
    var example = parse(cfg.example) || parse('2021-10-13');

    function q(sel) { return root.querySelector(sel); }

    var view = q('[data-ppv-view]');
    var nasa = q('[data-ppv-nasa]');
    var printHost = q('[data-ppv-print]');
    var mark = q('[data-ppv-mark]');
    var status = q('[data-ppv-status]');
    var retry = q('[data-ppv-retry]');
    var cap = q('[data-ppv-cap]');
    var dateOut = q('[data-ppv-date]');
    var printTag = q('[data-ppv-printtag]');
    var line = q('[data-ppv-line]');
    var open = q('[data-ppv-open]');
    var field = q('[data-pp-datefield]');
    var buttons = root.querySelectorAll('[data-ppv-planet]');
    var diyYears = q('[data-ppv-diy-years]');
    if (!view || !nasa || !printHost) return;

    var st = { date: null, planet: 'all' };
    var orrery = null;
    var token = 0;
    var timer = null;
    var shownSrc = '';

    function setStatus(state, text) {
      view.setAttribute('data-state', state);
      if (status) status.textContent = text || '';
    }

    function words() {
      var p = st.date || example;
      if (cap) cap.textContent = st.date ? (L.yours || '') : (L.example || '');
      if (dateOut) dateOut.textContent = longDate(p);
      if (printTag) printTag.textContent = st.date ? (L.printTagYours || '') : (L.printTag || '');
      if (line) {
        var name = st.planet.charAt(0).toUpperCase() + st.planet.slice(1);
        line.textContent = st.planet === 'all' ? (L.all || '') : (L.one || '').replace('[planet]', name);
      }
      each(buttons, function (b) {
        b.setAttribute('aria-pressed', b.getAttribute('data-ppv-planet') === st.planet ? 'true' : 'false');
      });
      /* NASA's form, filled in for the same picture */
      var vals = { month: MON[p.m - 1], day: String(p.d), year: String(p.y), fov: String(formFov(p, st.planet)) };
      each(root.querySelectorAll('[data-ppv-f]'), function (el) { el.textContent = vals[el.getAttribute('data-ppv-f')]; });
      if (diyYears) diyYears.hidden = p.y >= FORM_YEARS[0] && p.y <= FORM_YEARS[1];
    }

    /* The example images stay until the visitor gives a date or picks a
       planet; from then on both layers are live. */
    function show() {
      words();
      if (!st.date && st.planet === 'all') {
        view.setAttribute('data-mode', 'example');
        setStatus('ready');
        if (open) open.href = simURL(example, ALL_FOV);
        return;
      }
      var O = window.PPOrrery;
      if (!O) { window.addEventListener('load', show, { once: true }); return; }

      var p = st.date || example;
      var pl = plan(O, p, st.planet);
      var src = simURL(p, pl.fov);
      if (open) open.href = src;
      if (src === shownSrc && view.getAttribute('data-state') === 'ready') return;

      var my = ++token;
      setStatus('loading', L.loading);
      clearTimeout(timer);
      timer = setTimeout(function () { if (my === token) fail(); }, TIMEOUT_MS);

      var img = new Image();
      img.decoding = 'async';
      img.onload = function () {
        if (my !== token) return;
        clearTimeout(timer);
        nasa.src = src;
        nasa.alt = 'NASA JPL Solar System Simulator: the solar system seen from above on ' +
          longDate(p) + ' at 12:00 UTC';
        shownSrc = src;
        place(O, p, pl);
        view.setAttribute('data-mode', 'live');
        setStatus('ready');
      };
      img.onerror = function () {
        if (my !== token) return;
        clearTimeout(timer);
        fail();
      };
      img.src = src;
    }

    function fail() {
      shownSrc = '';
      setStatus('failed', L.failed);
    }

    function place(O, p, pl) {
      if (!orrery) {
        orrery = O.create(printHost, {
          theme: 'black',
          fit: 'poster',
          rules: false,
          date: p.iso,
          zoom: pl.zoom,
          viewBox: pl.viewBox
        });
      } else {
        orrery.setDate(p.iso);
        orrery.options.zoom = pl.zoom;
        orrery.setViewBox(pl.viewBox);
      }
      printHost.setAttribute('data-focus', st.planet);
      printHost.classList.toggle('is-focus', st.planet !== 'all');
      if (mark) {
        mark.hidden = !pl.mark;
        if (pl.mark) {
          mark.style.left = (pl.mark.x * 100) + '%';
          mark.style.top = (pl.mark.y * 100) + '%';
        }
      }
      if (pl.mark) sweep(pl.mark.x * 100);
    }

    /* Carry the divider once across the chosen planet, NASA's dot first,
       then the print's dot in the same gold ring. */
    var sweepRaf = null;
    function sweep(at) {
      var compare = q('[data-pp-compare]');
      if (!compare || !compare.ppCompareSet) return;
      var from = Math.min(100, at + 12), to = Math.max(0, at - 12);
      if (sweepRaf) cancelAnimationFrame(sweepRaf);
      if (reduce.matches) { compare.ppCompareSet(to); return; }
      var t0 = null, MS = 1400;
      compare.ppCompareSet(from);
      function frame(now) {
        if (t0 === null) t0 = now + 350;             /* a beat on NASA's dot first */
        var t = Math.max(0, Math.min(1, (now - t0) / MS));
        var e = t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
        compare.ppCompareSet(from + (to - from) * e);
        sweepRaf = t < 1 ? requestAnimationFrame(frame) : null;
      }
      sweepRaf = requestAnimationFrame(frame);
    }

    each(buttons, function (b) {
      b.addEventListener('click', function () {
        st.planet = b.getAttribute('data-ppv-planet');
        show();
      });
    });

    if (retry) {
      /* the slider captures any press inside the picture; keep this one */
      retry.addEventListener('pointerdown', function (e) { e.stopPropagation(); });
      retry.addEventListener('click', function () { shownSrc = ''; show(); });
    }

    root.addEventListener('pp:datechange', function (e) {
      var p = e.detail && parse(e.detail.value);
      if (!p) return;
      st.date = p;
      show();
      if (window.history && history.replaceState) {
        try {
          var u = new URL(window.location.href);
          u.searchParams.set('date', p.iso);
          history.replaceState(history.state, '', u.toString());
        } catch (e2) { /* a sandboxed frame may refuse; harmless */ }
      }
    });

    /* a date in the address fills the field on arrival */
    var given = null;
    try { given = parse(new URL(window.location.href).searchParams.get('date')); } catch (e3) { given = null; }
    if (given) {
      var setField = function () {
        if (!field || !field.ppField) return false;
        field.ppField.set(given.iso, true);
        return true;
      };
      st.date = given;
      if (!setField()) window.addEventListener('load', setField, { once: true });
    }

    show();
  }

  each(document.querySelectorAll('[data-ppv]'), init);
})();
